import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Filter, X } from 'lucide-react';
import ProductCard from '../components/product/ProductCard';
import ProductCardSkeleton from '../components/product/ProductCardSkeleton';
import Reveal from '../components/common/Reveal';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { productsApi, searchApi, categoriesApi, brandsApi } from '../services/api';
import { useLocalization } from '../contexts/LocalizationContext';
import { getItemListSchema, getBreadcrumbSchema, getCollectionPageSchema } from '../utils/seoSchemas';
import { uniqueWordForCategory } from '../utils/grammar';

// Categories/brands come from the versioned schema — name may be a plain
// string, a { [lang]: string } object, or nested under `versions[]`.
// Shared so the sidebar filter list and the SEO title stay in sync.
const getCategoryDisplayName = (category, currentLang) => {
  if (!category) return '';
  if (category.versions && category.versions.length > 0) {
    const firstVersion = category.versions.find(v => v.name) || category.versions[0];
    return firstVersion?.name || category.slug;
  }
  if (category.name) {
    if (typeof category.name === 'string') return category.name;
    return category.name[currentLang] || category.name.es || category.slug;
  }
  return category.slug;
};

const ShopPage = () => {
  const { t, i18n } = useTranslation();
  const { countryCode, formatPrice: locFormatPrice, loading: localizationLoading } = useLocalization();
  const { categorySlug } = useParams(); // Get category from URL path
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // priceRange = the shopper's own price filter (null until they move the slider, so no price limit is
  // ever sent by default — a fixed default would hide every product in countries with other currencies).
  const [priceRange, setPriceRange] = useState(null);
  const [priceBounds, setPriceBounds] = useState([0, 1000]);
  const maxPrice = priceBounds[1];
  const shownRange = priceRange || priceBounds;
  // The slider updates continuously while dragging; the server is only asked once it settles.
  const [appliedPriceRange, setAppliedPriceRange] = useState(null);
  useEffect(() => {
    if (priceRange === null) { setAppliedPriceRange(null); return undefined; }
    const timer = setTimeout(() => { setAppliedPriceRange(priceRange); setCurrentPage(1); }, 350);
    return () => clearTimeout(timer);
  }, [priceRange]);
  const productsRequestSeq = useRef(0);
  
  // Filters state
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [sortBy, setSortBy] = useState('createdAt_desc');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const limit = 24;
  const currentLang = i18n.language;

  useEffect(() => {
    // Wait for the real storefront country (the context starts on a default one while it loads)
    if (countryCode && !localizationLoading) {
      setPriceRange(null);
      fetchCategories();
      fetchBrands();
      fetchFilterOptions();
    }
  }, [countryCode, localizationLoading]);

  // Read category from URL path parameter or query parameter (backward compatibility)
  useEffect(() => {
    const categoryParam = categorySlug || searchParams.get('category');
    if (!categoryParam) {
      // /shop with no slug -> "Todos": always clear any leftover category filter
      // so the user sees every product (not the previously-selected category).
      if (selectedCategories.length > 0) {
        setSelectedCategories([]);
        setCurrentPage(1);
      }
      return;
    }
    if (categories.length === 0) return;
    const category = categories.find((cat) => cat.slug === categoryParam);
    if (!category) return;
    const categoryId = category._id || category.id;
    setSelectedCategories((prev) => (prev.length === 1 && prev[0] === categoryId ? prev : [categoryId]));
  }, [categorySlug, searchParams, categories]);

  useEffect(() => {
    if (countryCode && !localizationLoading) {
      fetchProducts();
    }
  }, [currentPage, selectedCategories, selectedBrands, appliedPriceRange, sortBy, countryCode, localizationLoading]);

  const fetchCategories = async () => {
    try {
      const data = await categoriesApi.getAll(true, countryCode);
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const data = await brandsApi.getAll(true, countryCode);
      setBrands(data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const data = await searchApi.getFilterOptions(countryCode);
      if (data.priceRange) {
        setPriceBounds([data.priceRange.min, data.priceRange.max]);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchProducts = async () => {
    const seq = ++productsRequestSeq.current;
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit,
        sort: sortBy,
        country: countryCode
      };
      if (appliedPriceRange) {
        params.min_price = appliedPriceRange[0];
        params.max_price = appliedPriceRange[1];
      }

      // Send ALL selected categories (OR logic)
      if (selectedCategories.length > 0) {
        params.categories = selectedCategories.join(','); // Send as comma-separated list
      }

      if (selectedBrands.length > 0) {
        params.brands = selectedBrands.join(','); // Send all brands too
      }

      const data = await productsApi.getAll(params);
      if (seq !== productsRequestSeq.current) return; // a newer request supersedes this one
      setProducts(data.products || []);
      setTotalProducts(data.total || 0);
    } catch (error) {
      if (seq !== productsRequestSeq.current) return;
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      if (seq === productsRequestSeq.current) setLoading(false);
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
    setCurrentPage(1);
  };

  const handleBrandToggle = (brandId) => {
    setSelectedBrands(prev => 
      prev.includes(brandId) 
        ? prev.filter(id => id !== brandId)
        : [...prev, brandId]
    );
    setCurrentPage(1);
  };

  const handlePriceChange = (value) => {
    setPriceRange(value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedBrands([]);
    setPriceRange(null);
    setCurrentPage(1);
  };

  const filtersContent = (
    <div className="space-y-6">
      {/* Active Filters */}
      {(selectedCategories.length > 0 || selectedBrands.length > 0) && (
        <div className="pb-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-base">{t('shop.activeFilters')}</h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearFilters}
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {t('shop.clearAll')}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedCategories.map(catId => {
              const cat = categories.find(c => (c._id || c.id) === catId);
              if (!cat) return null;
              
              const displayName = getCategoryDisplayName(cat, currentLang);

              return (
                <div 
                  key={catId} 
                  className="flex items-center gap-2 bg-[#be1f1a] text-white px-3 py-1.5 rounded-full text-sm hover:bg-[#a71b17] transition-colors"
                >
                  <span className="font-medium">{displayName}</span>
                  <button 
                    onClick={() => handleCategoryToggle(catId)}
                    className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                    aria-label={`Remove ${displayName} filter`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
            {selectedBrands.map(brandId => {
              const brand = brands.find(b => (b._id || b.id) === brandId);
              if (!brand) return null;
              
              // Extract brand name from versioned schema
              let displayName = brand.slug;
              if (brand.versions && brand.versions.length > 0) {
                const firstVersion = brand.versions.find(v => v.name) || brand.versions[0];
                displayName = firstVersion?.name || brand.slug;
              } else if (brand.name) {
                displayName = brand.name;
              }
              
              return (
                <div 
                  key={brandId} 
                  className="flex items-center gap-2 bg-[#be1f1a] text-white px-3 py-1.5 rounded-full text-sm hover:bg-[#a71b17] transition-colors"
                >
                  <span className="font-medium">{displayName}</span>
                  <button 
                    onClick={() => handleBrandToggle(brandId)}
                    className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                    aria-label={`Remove ${displayName} filter`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Categories Filter */}
      <div>
        <h3 className="font-semibold text-base mb-4">{t('categories.allCategories')}</h3>
        <div className="space-y-3">
          {categories.map(category => {
            const catId = category._id || category.id;
            // Extract category name from versioned schema
            const displayName = getCategoryDisplayName(category, currentLang);

            return (
              <div key={catId} className="flex items-start gap-2">
                <Checkbox
                  id={`cat-${catId}`}
                  checked={selectedCategories.includes(catId)}
                  onCheckedChange={() => handleCategoryToggle(catId)}
                  className="mt-0.5"
                />
                <Label 
                  htmlFor={`cat-${catId}`} 
                  className="cursor-pointer text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1"
                >
                  {displayName}
                </Label>
              </div>
            );
          })}
        </div>
      </div>

      {/* Brands Filter */}
      {brands.length > 0 && (
        <div>
          <h3 className="font-semibold text-base mb-4">{t('brands.title')}</h3>
          <div className="space-y-3">
            {brands.map(brand => {
              const brandId = brand._id || brand.id;
              // Extract brand name from versioned schema
              let displayName = brand.slug;
              if (brand.versions && brand.versions.length > 0) {
                const firstVersion = brand.versions.find(v => v.name) || brand.versions[0];
                displayName = firstVersion?.name || brand.slug;
              } else if (brand.name) {
                displayName = brand.name;
              }
              
              return (
                <div key={brandId} className="flex items-start gap-2">
                  <Checkbox
                    id={`brand-${brandId}`}
                    checked={selectedBrands.includes(brandId)}
                    onCheckedChange={() => handleBrandToggle(brandId)}
                    className="mt-0.5"
                  />
                  <Label 
                    htmlFor={`brand-${brandId}`} 
                    className="cursor-pointer text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1"
                  >
                    {displayName}
                  </Label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Price Range Filter */}
      <div>
        <h3 className="font-semibold text-base mb-4">{t('shop.priceRange')}</h3>
        <div className="space-y-4">
          <Slider
            value={shownRange}
            onValueChange={handlePriceChange}
            max={maxPrice}
            step={10}
            className="w-full"
          />
          <div className="flex justify-between text-sm text-gray-600">
            <span>{locFormatPrice(shownRange[0])}</span>
            <span>{locFormatPrice(shownRange[1])}</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Get current category name for SEO. `currentCategory` is already
  // resolved from `selectedCategories` (set by the effect above from
  // EITHER the path param `/shop/:categorySlug` OR the legacy query
  // param `/shop?category=`), so basing title/description/canonical/
  // breadcrumbs on it — rather than the raw `categorySlug` path param
  // alone — keeps them consistent regardless of which URL form got you
  // here. Previously, `/shop?category=x` rendered x's products but
  // still claimed the generic "/shop" page as its own title AND its
  // canonical URL — a real duplicate-content mismatch (Search Console:
  // "Duplicate without user-selected canonical" / "Google chose a
  // different canonical than user").
  const currentCategory = categories.find(cat => (cat._id || cat.id) === (selectedCategories[0]));
  const categoryName = currentCategory
    ? getCategoryDisplayName(currentCategory, currentLang)
    : t('categories.allCategories');
  const pageTitle = currentCategory ? `${categoryName} — ${t('shop.title')}` : t('shop.title');
  const pageDescription = currentCategory
    ? t('shop.browseCategoryDescription', { category: categoryName })
    : t('shop.discoverCollectionDescription');

  // Breadcrumb data for SEO
  const breadcrumbItems = [
    { name: t('nav.home') || 'Home', url: '/' },
    { name: t('shop.title'), url: '/shop' }
  ];
  if (currentCategory) {
    breadcrumbItems.push({ name: categoryName, url: `/shop/${currentCategory.slug}` });
  }

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        url={currentCategory ? `/shop/${currentCategory.slug}` : '/shop'}
        type="website"
      />
      
      {/* Structured Data */}
      {products.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(getItemListSchema(products, categoryName))}
        </script>
      )}
      <script type="application/ld+json">
        {JSON.stringify(getBreadcrumbSchema(breadcrumbItems))}
      </script>
      
      <div className="min-h-screen" style={{ background: 'var(--bg)' }}>
      <div className="container mx-auto px-4 sm:px-6 md:px-10 py-10 md:py-14">
        {/* Breadcrumbs */}
        <nav className="mb-10" style={{ fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-body)' }}>
          <ol className="flex items-center gap-3">
            <li><a href="/" style={{ color: 'var(--muted-editorial)' }} className="hover:underline">{t('common.home')}</a></li>
            <li style={{ color: 'var(--line)' }}>/</li>
            <li><a href="/shop" style={{ color: currentCategory ? 'var(--muted-editorial)' : 'var(--ink)' }} className="hover:underline">{t('nav.shop')}</a></li>
            {currentCategory && (
              <>
                <li style={{ color: 'var(--line)' }}>/</li>
                <li style={{ color: 'var(--ink)', fontWeight: 500 }}>{categoryName}</li>
              </>
            )}
          </ol>
        </nav>

        {/* Page Header — editorial */}
        <div className="mb-12 text-center">
          <span className="eyebrow" style={{ display: 'block', marginBottom: '16px' }}>
            {t('shopExtra.collection')}
          </span>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(48px, 7vw, 96px)',
              fontWeight: 300,
              lineHeight: 0.95,
              letterSpacing: '-2px',
              color: 'var(--ink)',
              marginBottom: '16px',
            }}
          >
            {currentCategory ? (
              <>{categoryName} <em>{uniqueWordForCategory(currentCategory.slug)}</em></>
            ) : (
              <>{t('shopExtra.allPieces')} <em>{t('shopExtra.allPiecesEm')}</em></>
            )}
          </h1>
          <p
            style={{
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              fontVariationSettings: "'opsz' 60, 'SOFT' 100",
              fontSize: '17px',
              color: 'var(--muted-editorial)',
              maxWidth: '560px',
              margin: '0 auto',
            }}
          >
            {currentCategory
              ? t('shop.browseCategoryProducts', { category: categoryName })
              : t('shop.discoverCollection')}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop Filters Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              {filtersContent}
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                {/* Mobile Filter Button */}
                <Sheet open={isMobileFilterOpen} onOpenChange={setIsMobileFilterOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="lg:hidden">
                      <Filter className="h-4 w-4 mr-2" />
                      {t('shop.filters')}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-80 overflow-y-auto">
                    <SheetHeader>
                      <SheetTitle>{t('shop.filters')}</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      {filtersContent}
                    </div>
                  </SheetContent>
                </Sheet>

                <p style={{ fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', color: 'var(--muted-editorial)', fontFamily: 'var(--font-body)' }}>
                  {totalProducts} {totalProducts === 1 ? t('shop.product') : t('shop.products')}
                </p>
              </div>

              {/* Sort Dropdown */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder={t('shop.sortBy')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="createdAt_desc">{t('shop.sortOptions.newest')}</SelectItem>
                  <SelectItem value="createdAt_asc">{t('common.oldest')}</SelectItem>
                  <SelectItem value="price_asc">{t('shop.sortOptions.priceLowHigh')}</SelectItem>
                  <SelectItem value="price_desc">{t('shop.sortOptions.priceHighLow')}</SelectItem>
                  <SelectItem value="name_asc">A-Z</SelectItem>
                  <SelectItem value="name_desc">Z-A</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Products Grid — editorial rhythm: 3 cols, then 2 cols (featured), repeat.
                Block of 8 = 6 products in 3-col + 2 products in 2-col larger. */}
            {loading ? (
              // Skeleton mirrors the real 6+2 editorial rhythm so the grid
              // doesn't visually shift when data arrives. Uses branded
              // shimmer blocks instead of the previous gray pulse.
              <div className="space-y-6" data-testid="shop-products-skeleton">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {[...Array(6)].map((_, i) => (
                    <ProductCardSkeleton key={`sk-t-${i}`} />
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {[...Array(2)].map((_, i) => (
                    <ProductCardSkeleton key={`sk-f-${i}`} variant="featured" />
                  ))}
                </div>
              </div>
            ) : products.length > 0 ? (
              <>
                <div className="space-y-6" data-testid="shop-products-grid">
                  {(() => {
                    const BLOCK = 8; // 6 + 2
                    const blocks = [];
                    for (let i = 0; i < products.length; i += BLOCK) {
                      const triple = products.slice(i, i + 6);
                      const featured = products.slice(i + 6, i + 8);
                      if (triple.length > 0) {
                        blocks.push(
                          <Reveal
                            key={`t-${i}`}
                            stagger
                            y={24}
                            duration={0.65}
                            start="top 92%"
                            className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
                          >
                            {triple.map((p, j) => (
                              <ProductCard key={p.id} product={p} priority={i === 0 && j < 3} />
                            ))}
                          </Reveal>
                        );
                      }
                      if (featured.length > 0) {
                        blocks.push(
                          <Reveal
                            key={`f-${i}`}
                            stagger
                            y={28}
                            duration={0.7}
                            start="top 92%"
                            className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6"
                            testId="shop-featured-row"
                          >
                            {featured.map((p) => (
                              <ProductCard key={p.id} product={p} />
                            ))}
                          </Reveal>
                        );
                      }
                    }
                    return blocks;
                  })()}
                </div>

                {/* Pagination */}
                {totalProducts > limit && (
                  <div className="mt-12 flex justify-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      {t('common.previous')}
                    </Button>
                    <div className="flex items-center gap-2">
                      {[...Array(Math.ceil(totalProducts / limit))].map((_, i) => (
                        <Button
                          key={i}
                          variant={currentPage === i + 1 ? 'default' : 'outline'}
                          onClick={() => setCurrentPage(i + 1)}
                          style={currentPage === i + 1 ? { background: 'var(--ink)', color: 'var(--bg)', borderColor: 'var(--ink)' } : undefined}
                        >
                          {i + 1}
                        </Button>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={currentPage >= Math.ceil(totalProducts / limit)}
                    >
                      {t('common.next')}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-24">
                <h3
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontVariationSettings: "'opsz' 60, 'SOFT' 50",
                    fontSize: '32px',
                    color: 'var(--ink)',
                    marginBottom: '12px',
                  }}
                >
                  {t('shopExtra.noPieces')} <em>{t('shopExtra.noPiecesEm')}</em>
                </h3>
                <p style={{ color: 'var(--ink-soft)', marginBottom: '28px' }}>
                  {t('shop.noProducts')}
                </p>
                <button onClick={clearFilters} className="btn-outline" type="button">
                  {t('shop.clearFilters')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ShopPage;